import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ConfigProvider, Table, Select, Input } from 'antd';
import type { TableColumnsType, ThemeConfig } from 'antd';
import { fontFamily } from '../../../theme/tokens';
import { PlusIcon } from '../../../assets/icons';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { usePlatformState, setPlatformState } from '../../../features/platform/platformStateSlice';
import OrganizationsPage from '../OrganizationsPage';
import {
  useGetStatesQuery,
  useGetStatesCatalogQuery,
  useCreateStateMutation,
  useUpdateStateMutation,
  useDeleteStateMutation,
  useGetStateDepartmentsQuery,
  useGetReviewQueueQuery,
  useGetTemplatesQuery,
} from '../../../features/platform/platformApi';
import type { PlatformStateRow, StateStatus } from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — States overview (prototype `isOverview`).
 * Add a state, onboard it in six steps, then mark it live. Templates are
 * global; each state has its own profile, territories and people.
 * Wired to the Platform Admin API.
 * ------------------------------------------------------------------ */

type Status = StateStatus;

const STATUS_LABEL: Record<Status, string> = {
  notstarted: 'Not started',
  onboarding: 'Onboarding',
  live: 'Live',
  paused: 'Paused',
};
const STATUS_STYLE: Record<Status, string> = {
  notstarted: 'bg-muted-bg text-ink-secondary',
  onboarding: 'bg-[#fffbeb] text-[#b45309]',
  live: 'bg-brand-shade text-brand',
  paused: 'bg-[#fee2e2] text-[#dc2626]',
};

/** antd Table theme, mapped to the Chunaav / Figma design tokens (shared with other admin tables). */
const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: 'rgba(195,198,214,0.2)', fontFamily },
  components: {
    Table: {
      headerBg: 'rgba(209,226,255,0.5)',
      headerColor: '#46556F',
      headerSplitColor: 'transparent',
      borderColor: 'rgba(195,198,214,0.2)',
      cellPaddingBlock: 18,
      cellPaddingInline: 24,
      rowHoverBg: '#f7faff',
      headerBorderRadius: 0,
      colorText: '#00173e',
      fontSize: 14,
    },
  },
};

/** Secondary pill button used for the row actions. */
const ROW_BTN =
  'h-9 shrink-0 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60';
const ROW_PRIMARY = `${ROW_BTN} bg-brand text-white shadow-btn hover:bg-brand-dark`;
const ROW_OUTLINE = `${ROW_BTN} border border-line text-ink hover:bg-brand-shade`;
const ROW_DANGER = `${ROW_BTN} border border-[#fecaca] bg-white text-[#dc2626] hover:bg-[#fef2f2]`;
const ROW_DANGER_SOLID = `${ROW_BTN} bg-[#dc2626] text-white hover:bg-[#b91c1c]`;

function StatusPill({ status }: { status: Status }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.55px]',
        STATUS_STYLE[status],
      ].join(' ')}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

export default function StatesOverviewPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: states = [], isLoading } = useGetStatesQuery();
  const { data: catalog = [] } = useGetStatesCatalogQuery();
  const [createState] = useCreateStateMutation();
  const [updateState] = useUpdateStateMutation();
  const [deleteState] = useDeleteStateMutation();

  const { code: selected } = usePlatformState();

  const [addOpen, setAddOpen] = useState(false);
  const [addCode, setAddCode] = useState<string | undefined>();
  const [addLang, setAddLang] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const addOptions = useMemo(
    () =>
      catalog
        .filter((c) => !states.some((s) => s.code === c.code))
        .map((c) => ({ value: c.code, label: c.name })),
    [catalog, states],
  );

  const addState = async () => {
    if (!addCode) return;
    await createState({ code: addCode }).unwrap();
    setAddOpen(false);
    setAddCode(undefined);
    setAddLang('');
  };

  const removeState = async (code: string) => {
    await deleteState(code).unwrap();
    setConfirmRemove(null);
  };

  const selectedState = states.find((s) => s.code === selected) ?? states[0];
  const liveCount = states.filter((s) => s.status === 'live').length;
  const onboardingCount = states.filter((s) => s.status === 'onboarding').length;

  /* Cheap summary queries for the "At a glance" cards. */
  const { data: depts = [] } = useGetStateDepartmentsQuery(selected, { skip: !selected });
  const { data: reviewPending = [] } = useGetReviewQueueQuery(
    { code: selected, status: 'pending' },
    { skip: !selected },
  );
  const { data: templates = [] } = useGetTemplatesQuery();

  const deptsEnabled = depts.filter((d) => d.code !== 'party').length;
  const templatesValue = templates.length ? String(templates.length) : '25';

  const cards = [
    {
      title: 'Templates (all states)',
      value: templatesValue,
      sub: 'global department templates',
      go: '/admin/templates',
    },
    { title: 'Standard sources', value: '3 of 3', sub: 'ECI, SEC and LGD imported (sample)', go: '/admin/onboarding' },
    {
      title: `${selectedState?.name ?? ''} profile`,
      value: `${deptsEnabled} of 25`,
      sub: 'departments enabled',
      go: '/admin/departments',
    },
    { title: 'Territories', value: '74%', sub: 'average coverage of own levels', go: '/admin/work-queues' },
    { title: 'People', value: '12 of 40', sub: 'posts with a person recorded', go: '/admin/work-queues' },
    { title: 'Review queue', value: String(reviewPending.length), sub: 'client submissions waiting', go: '/admin/review-queue' },
  ];

  const columns: TableColumnsType<PlatformStateRow> = [
    {
      title: 'State',
      key: 'state',
      render: (_, s) => {
        const isSel = s.code === selected;
        return (
          <button
            type="button"
            onClick={() => dispatch(setPlatformState({ code: s.code, name: s.name }))}
            className="flex flex-col items-start gap-0.5 text-left"
          >
            <span className="text-sm font-bold text-ink">{s.name}</span>
            <span className="text-xs text-ink-secondary">
              {s.code}
              {isSel ? ' · Selected' : ''}
            </span>
          </button>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, s) => <StatusPill status={s.status} />,
    },
    {
      title: 'Onboarding',
      key: 'onboarding',
      width: 200,
      render: (_, s) => {
        const pct = typeof s.progress === 'number' ? s.progress : 0;
        const steps = typeof s.steps_done === 'number' ? s.steps_done : 0;
        return (
          <div className="flex flex-col gap-1.5">
            <span className="block h-1.5 overflow-hidden rounded-full bg-muted-bg">
              <span className="block h-1.5 rounded-full bg-brand" style={{ width: `${pct}%` }} />
            </span>
            <span className="text-xs text-ink-secondary">
              {pct}% · {steps} of 6 steps
            </span>
          </div>
        );
      },
    },
    { title: 'Clients', key: 'clients', width: 90, render: (_, s) => <span className="text-sm text-ink">{typeof s.clients === 'number' ? s.clients : 0}</span> },
    { title: 'Language', key: 'lang', width: 120, render: (_, s) => <span className="text-sm text-ink">{typeof s.language === 'string' ? s.language : '—'}</span> },
    {
      title: '',
      key: 'actions',
      width: 300,
      align: 'right',
      render: (_, s) => {
        // Removable when it has no clients and isn't the active state.
        const removable = s.clients === 0 && s.code !== selected;
        if (confirmRemove === s.code) {
          return (
            <div
              className="flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-xs font-semibold text-[#dc2626]">Remove {s.name} and all its data?</span>
              <button type="button" onClick={() => removeState(s.code)} className={ROW_DANGER_SOLID}>
                Remove
              </button>
              <button type="button" onClick={() => setConfirmRemove(null)} className={ROW_OUTLINE}>
                Keep
              </button>
            </div>
          );
        }
        return (
          <div
            className="flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            {s.status === 'notstarted' && (
              <button
                type="button"
                onClick={() => updateState({ code: s.code, patch: { status: 'onboarding' } })}
                className={ROW_PRIMARY}
              >
                Start onboarding
              </button>
            )}
            {s.status !== 'notstarted' && (
              <button
                type="button"
                onClick={() => {
                  dispatch(setPlatformState({ code: s.code, name: s.name }));
                  navigate('/admin/onboarding');
                }}
                className={ROW_OUTLINE}
              >
                Open onboarding
              </button>
            )}
            {removable && (
              <button type="button" onClick={() => setConfirmRemove(s.code)} className={ROW_DANGER}>
                Remove
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: 'All states' }]} />
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-brand">PLATFORM ADMIN</span>
          <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">States</h1>
          <p className="max-w-[760px] text-sm leading-6 text-ink-secondary">
            Templates are global. Each state has its own profile, territories and people, shared by
            every client in that state. Add a state and onboard it in six steps.
          </p>
          <span className="text-[13px] font-semibold text-ink">
            {states.length} states · {liveCount} live · {onboardingCount} onboarding
          </span>
        </div>
        {!addOpen && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="flex h-10 shrink-0 items-center justify-center gap-2.5 self-start rounded-lg bg-brand px-6 text-[13px] font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-[16.667px]" />
            Add a state
          </button>
        )}
      </div>

      {/* Add a state */}
      {addOpen && (
        <div className="flex flex-col gap-3 rounded-xl border border-brand bg-white p-6 shadow-card">
          <h2 className="text-base font-bold text-ink">Add a state</h2>
          <span className="text-[13px] text-ink-secondary">
            The state starts empty: no templates enabled, no territories, no people. Onboarding begins
            when you start it.
          </span>
          <div className="flex flex-wrap items-end gap-3">
            <div className="form-field-select flex flex-1 basis-[280px] flex-col gap-1.5">
              <label className="text-xs font-semibold text-ink-secondary">State or union territory</label>
              <Select
                className="w-full"
                size="large"
                showSearch
                optionFilterProp="label"
                placeholder="Choose a state or union territory…"
                value={addCode}
                onChange={(v) => {
                  const match = catalog.find((c) => c.code === v);
                  setAddCode(v);
                  setAddLang(match && typeof match.language === 'string' ? match.language : '');
                }}
                options={addOptions}
              />
            </div>
            <div className="flex basis-[200px] flex-col gap-1.5">
              <label className="text-xs font-semibold text-ink-secondary">Document language (OCR)</label>
              <Input
                size="large"
                value={addLang}
                onChange={(e) => setAddLang(e.target.value)}
                placeholder="e.g. Marathi"
              />
            </div>
            <button
              type="button"
              onClick={addState}
              disabled={!addCode}
              className="h-11 rounded-lg bg-brand px-5 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              Add state
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="h-11 rounded-lg border border-line px-5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* States table */}
      {isLoading ? (
        <span className="text-sm text-ink-secondary">Loading states…</span>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
          <ConfigProvider theme={TABLE_THEME}>
            <Table<PlatformStateRow>
              className="chunaav-table"
              columns={columns}
              dataSource={states}
              rowKey="code"
              pagination={false}
              scroll={{ x: 'max-content' }}
              // Clicking a row makes it the active state everywhere (sidebar, departments, clients list).
              onRow={(s) => ({ onClick: () => dispatch(setPlatformState({ code: s.code, name: s.name })) })}
              rowClassName={(s) => ['cursor-pointer', s.code === selected ? 'bg-brand-shade/40' : ''].join(' ')}
            />
          </ConfigProvider>
        </div>
      )}

      {/* At a glance */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-ink">{selectedState?.name} at a glance</h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
          {cards.map((c) => (
            <div key={c.title} className="flex flex-col gap-1 rounded-xl border border-line/30 bg-white p-4 shadow-card">
              <span className="text-xs font-semibold text-ink-secondary">{c.title}</span>
              <span className="text-[26px] font-bold text-ink">{c.value}</span>
              <span className="text-xs text-ink-secondary">{c.sub}</span>
              <button
                type="button"
                onClick={() => navigate(c.go)}
                className="mt-1.5 h-9 self-start rounded-lg border border-line px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
              >
                Open
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Clients — organizations for the selected state. */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-ink">Clients in {selectedState?.name}</h2>
        <OrganizationsPage embedded stateCode={selected} canCreate />
      </div>
    </div>
  );
}
